// Centralized helper for picking the right marketing-master derivative
// for a given UI surface. Backend exposes
// `marketingImageDerivatives` on agent and public project payloads
// (Task #917). When a derivative is unavailable (legacy projects, or
// before a master has been uploaded) callers fall back to the legacy
// `coverImageUrl`.

export type DerivativeKey =
  | "cover_thumbnail"
  | "cover_header"
  | "og_landscape"
  | "og_square"
  | "flyer_hero"
  | "one_pager_hero";

export interface ProjectImageInput {
  coverImageUrl?: string | null;
  coverImage?: string | null;
  marketingImageDerivatives?: unknown;
}

/**
 * Pick a marketing-master derivative URL for the given surface key.
 *
 * @param derivativeOnly - When true, returns null instead of falling back to
 *   the legacy `coverImageUrl` / `coverImage` fields. Use this when you want
 *   to chain multiple derivative checks before accepting the legacy fallback,
 *   e.g.:
 *     pickProjectImage(p, "cover_header", { derivativeOnly: true })
 *       ?? pickProjectImage(p, "cover_thumbnail")
 *   gives: cover_header → cover_thumbnail → coverImageUrl (correct order).
 */
export function pickProjectImage(
  project: ProjectImageInput | null | undefined,
  key: DerivativeKey,
  { derivativeOnly = false }: { derivativeOnly?: boolean } = {},
): string | null {
  if (!project) return null;
  const derivs = project.marketingImageDerivatives;
  if (derivs && typeof derivs === "object") {
    const rec = (derivs as Record<string, { fileUrl?: string }>)[key];
    if (rec && rec.fileUrl) return rec.fileUrl;
  }
  if (derivativeOnly) return null;
  return project.coverImageUrl ?? project.coverImage ?? null;
}
